import React, { useEffect, useState } from "react";
import RegisterError from "@/components/common/register-error";
import AppleButton from "@/components/ui/apple-button";
import FacebookButton from "@/components/ui/facebook-button";
import { Button } from "@/components/ui/button";
import { FaLock } from "react-icons/fa";
import styles from "./styles/Register.module.css";
import { useMetrica } from "@/lib/next-yandex-metrica";
import Toggle from "@/components/ui/toggle";
import { api } from "@/services/api";

const failStyle = { border: "1px solid #ff4900" } as const;

export function FormStep({
  t,
  registerT,
  guestElo,
  email,
  password,
  emailStyle,
  passwordStyle,
  onEmailChange,
  onPasswordChange,
  onSetNews,
  onSubmit,
  onSignInFB,
  onSignInApple,
  guestLink,
}: {
  t: (key: string) => string;
  registerT: (key: string) => string;
  guestElo?: number | null;
  email: string;
  password: string;
  emailStyle?: typeof failStyle;
  passwordStyle?: typeof failStyle;
  onEmailChange: (val: string) => void;
  onPasswordChange: (val: string) => void;
  onSetNews: (val: number) => void;
  onSubmit: () => void;
  onSignInFB: () => void;
  onSignInApple: () => void;
  guestLink?: React.ReactNode;
}) {
  return (
    <div
      className={"flex flex-col justify-center items-stretch " + styles.body}
    >
      <div id="gbtn" className="w-full h-[40px] max-w-[400px] mx-auto"></div>
      <div className="my-1.5" />
      <div className="flex justify-center items-center md:block">
        <FacebookButton
          onClick={onSignInFB}
          text={t("Continue with Facebook")}
        />
      </div>
      <div
        className={`${styles.appleBtn} flex items-center justify-center md:block`}
      >
        <AppleButton onClick={onSignInApple} text={t("Continue with Apple")} />
      </div>
      <div className={styles.line}>
        <span>{registerT("or")}</span>
      </div>

      {guestElo !== null && guestElo !== undefined && (
        <div className={styles.elo}>
          <label className={styles.label}>{t("Rating")}</label>
          <input disabled type="text" value={guestElo} />
        </div>
      )}

      <label htmlFor="email" className={styles.label}>
        {t("Email")}
      </label>
      <input
        id="email"
        type="email"
        value={email}
        style={emailStyle as any}
        onChange={(e) => onEmailChange(e.target.value)}
      />

      <label htmlFor="password" className={styles.label}>
        {t("Password")}
      </label>
      <input
        id="password"
        type="password"
        value={password}
        style={passwordStyle as any}
        onChange={(e) => onPasswordChange(e.target.value)}
      />

      <div className={styles.news}>
        <input
          onChange={(e) => onSetNews(e.target.checked ? 1 : 0)}
          name="news"
          id="newsletter"
          type="checkbox"
          className="cursor-pointer"
        />
        <label htmlFor="newsletter" className="cursor-pointer select-none">
          {registerT("Get chess lessons and tips by email")}
        </label>
      </div>

      <div className="flex justify-center items-center md:block">
        <Button
          size="lg"
          className="py-7 mt-4 w-full text-lg font-bold"
          onClick={onSubmit}
        >
          {t("Continue")}
        </Button>
      </div>
      <div className="flex gap-1 justify-center items-center mt-3 text-sm text-altgray-600">
        {guestLink ? (
          guestLink
        ) : (
          <>
            <FaLock className="w-3 h-3" /> {registerT("No spam Cancel anytime")}
          </>
        )}
      </div>
    </div>
  );
}

export function UsernameStep({
  t,
  registerT,
  username,
  usernameStyle,
  usernameError,
  onUsernameChange,
  onSubmit,
}: {
  t: (key: string) => string;
  registerT: (key: string) => string;
  username: string;
  usernameStyle?: typeof failStyle;
  usernameError: string | null;
  onUsernameChange: (val: string) => void;
  onSubmit: () => void;
}) {
  return (
    <div
      className={
        "min-h-[450px] flex flex-col justify-center items-center " + styles.body
      }
    >
      <div className="flex flex-col items-stretch mt-4 w-full">
        <label htmlFor="username" className={"mb-1 " + styles.label}>
          {registerT("Username")}
        </label>
        <input
          autoFocus
          type="text"
          id="username"
          className="h-[48px] text-lg"
          style={usernameStyle}
          value={username}
          onChange={(e) => onUsernameChange(e.target.value)}
        />
        {usernameError ? (
          <RegisterError errorCode={usernameError} />
        ) : (
          <div className="h-[28px]"></div>
        )}

        <Button
          size="lg"
          className="py-7 mt-8 w-full text-lg font-bold"
          onClick={onSubmit}
        >
          {registerT("Complete")}
        </Button>
      </div>
    </div>
  );
}

export function JoinedStep({
  t,
  registerT,
  signedUpWithEmail,
  onClose,
  router,
  redirectAfterRegistration,
}: {
  t: (key: string) => string;
  registerT: (key: string) => string;
  signedUpWithEmail: boolean;
  onClose?: () => void;
  router?: any;
  redirectAfterRegistration?: () => void;
}) {
  const isPopup = !!onClose;
  const [receiveNews, setReceiveNews] = useState(false);

  const { reachGoal } = useMetrica();

  // Fire a generic signup completion goal for funnels
  useEffect(() => {
    reachGoal("signup_complete");
  }, [reachGoal]);

  const handleNewsToggle = (active: boolean) => {
    setReceiveNews(active);
    api.post("/users/news", { news: active }).catch((err) => {
      console.error("Failed to update news preference:", err);
    });
  };

  const handleButtonClick = () => {
    if (redirectAfterRegistration) {
      // Custom redirect callback provided
      // The callback should handle closing the popup itself
      redirectAfterRegistration();
      // Don't call onClose here - let the callback handle it
      // This prevents the popup from closing before the callback completes
    } else if (isPopup && onClose) {
      // Popup mode: close the popup
      onClose();
    } else if (router) {
      // Page mode: redirect back if returnTo provided, else lobby
      const q = router.query || {};
      const returnTo =
        (Array.isArray(q.returnTo) ? q.returnTo[0] : q.returnTo) || null;
      router.push(returnTo || "/lobby");
    }
  };

  return (
    <div
      className={
        "flex flex-col justify-center items-center w-full min-h-[510px]"
      }
    >
      <div className="flex overflow-hidden justify-center items-center rounded-full p-2 bg-[#f45100]">
        <img src="/icon-192x192.png" alt="Chess Hotel" width="80" height="80" />
      </div>
      <div className="mt-5 text-lg font-bold">
        {registerT("Welcome to ChessHotel")}
      </div>
      <div className="mt-2 text-altgray-600">
        {registerT("Compete in the Chess Leagues")}
      </div>
      {signedUpWithEmail && (
        <div className="mt-4 text-altgray-600">
          {registerT("Please check your email to confirm your account")}
        </div>
      )}
      <div className="flex gap-3 justify-center items-center mt-8">
        <Toggle onToggle={handleNewsToggle} active={receiveNews} />
        <span className="text-base text-altgray-600">
          {registerT("Get personalized chess tips")}
        </span>
      </div>
      <div className="flex justify-center items-center mt-12">
        <Button
          size="lg"
          className="py-7 mt-4 text-lg font-bold"
          onClick={handleButtonClick}
        >
          {isPopup ? t("Done") : t("Play")}
        </Button>
      </div>
    </div>
  );
}
